import { useId, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSwitchTenant } from '../api/admin';
import { useSolutions } from '../api/plans';
import { useDeleteTenant, useTenantOverview, useUpdateTenant } from '../api/tenants';
import { ApiError, errorMessage } from '../api/client';
import {
  TENANT_MAX_MEMBERS,
  displayName,
  type Tenant,
  type TenantOverview,
  type TenantStatus,
} from '../api/types';
import { can, hasAccess, homeFor, ownTenantPath, useMe } from '../auth/AuthContext';
import { subscriptionTone } from '../components/plans/PlanCards';
import { EmptyState, ErrorState, SkeletonStats, SkeletonTable } from '../components/states';
import { DataTable } from '../components/table/DataTable';
import { AccountManager } from '../components/tenants/AccountManager';
import { TenantTeam } from '../components/tenants/TenantTeam';
import {
  Alert,
  Badge,
  Button,
  Card,
  Modal,
  PageHeader,
  Section,
  SelectField,
  Stat,
  TextField,
  formatDate,
  shortId,
  statusLabel,
  statusTone,
} from '../components/ui';
import { ACCOUNT_STATUS_COPY, QualityBadge } from '../components/whatsapp/shared';

const TENANT_STATUSES: TenantStatus[] = ['PENDING', 'ACTIVE', 'SUSPENDED', 'CLOSED'];

/**
 * /tenant/:tenantId — one tenant: its settings, accounts, email domains,
 * plans, onboarding checklist and WhatsApp number, from one overview call.
 * Staff open any tenant (admins also edit its settings and offboard it); a
 * tenant account sees the same page for its own organization and is sent
 * there when it opens another id. The API refuses foreign ids either way.
 */
export function TenantDetail() {
  const me = useMe();
  const { tenantId = '' } = useParams();
  const isStaff = hasAccess(me, 'staff');
  const own = ownTenantPath(me);

  // A tenant account: only its own tenant. No tenant yet: nothing to show here.
  if (!isStaff) {
    if (!own) return <Navigate to={homeFor(me)} replace />;
    if (own !== `/tenant/${tenantId}`) return <Navigate to={own} replace />;
  }
  return <TenantLoader tenantId={tenantId} isStaff={isStaff} />;
}

function TenantLoader({ tenantId, isStaff }: { tenantId: string; isStaff: boolean }) {
  const overview = useTenantOverview(tenantId);
  if (overview.isPending) {
    return (
      <>
        <PageHeader eyebrow="Tenant" title={shortId(tenantId)} />
        <SkeletonStats count={4} />
      </>
    );
  }
  if (overview.isError) {
    // 400: not even a uuid; 403 / 404: not a tenant this account may open.
    const status = overview.error instanceof ApiError ? overview.error.status : null;
    if (status === 400 || status === 403 || status === 404) {
      return <NotFound tenantId={tenantId} isStaff={isStaff} />;
    }
    return (
      <>
        <PageHeader eyebrow="Tenant" title={shortId(tenantId)} />
        <ErrorState
          error={overview.error}
          onRetry={() => overview.refetch()}
          retrying={overview.isFetching}
        />
      </>
    );
  }
  return <TenantView key={tenantId} overview={overview.data} isStaff={isStaff} />;
}

function NotFound({ tenantId, isStaff }: { tenantId: string; isStaff: boolean }) {
  return (
    <>
      <PageHeader eyebrow="Tenant" title="No tenant here" />
      <EmptyState
        title="Nothing at this address"
        body={
          <>
            There is no tenant with the id <code>{tenantId}</code>
            {isStaff ? '' : ' that your account may open'}.
          </>
        }
        action={
          <Link to={isStaff ? '/staff/tenants' : '/portal'}>
            {isStaff ? 'Back to tenants' : 'Back to the portal'}
          </Link>
        }
      />
    </>
  );
}

function TenantView({ overview, isStaff }: { overview: TenantOverview; isStaff: boolean }) {
  const me = useMe();
  const { tenant, members, domains, subscriptions, onboarding, whatsapp } = overview;
  const solutions = useSolutions({ enabled: isStaff || can(me, 'tenant:billing:read') });
  const switchTenant = useSwitchTenant();
  const [notice, setNotice] = useState<ReactNode>(null);

  const live = subscriptions.filter((s) => s.status !== 'CANCELED');
  const liveFor = (solution: string) => live.find((s) => s.solution === solution) ?? null;
  const account = whatsapp.account;
  const activeDomains = domains.filter((d) => d.status === 'ACTIVE').length;
  const { progress } = onboarding;
  // The first three items still to activate, in the checklist's own order.
  const nextUp = onboarding.categories
    .flatMap((c) => c.items)
    .filter((i) => !i.activated)
    .slice(0, 3);
  const owner = members.find((m) => m.user.id === tenant.tenantOwnerId)?.user ?? null;

  // Where each card's "manage" link goes: the console for staff, the portal for the tenant.
  // Staff has no plans page; the plan is read here.
  const links = isStaff
    ? { domains: '/staff/domains', plans: null, whatsapp: '/staff/whatsapp' }
    : { domains: '/portal/email', plans: '/portal/subscription', whatsapp: '/portal/whatsapp' };

  const workOn = () =>
    switchTenant.mutate(tenant.id, {
      onSuccess: (context) =>
        setNotice(
          <>
            The console now works on <strong>{context.selectedTenant?.name ?? tenant.name}</strong>.
            Its accounts, plans and WhatsApp follow on the console pages.
          </>,
        ),
    });

  return (
    <>
      <PageHeader
        eyebrow={isStaff ? 'Tenant' : 'Your organization'}
        title={tenant.name}
        description={
          <>
            <code>{tenant.slug}</code>{' '}
            <Badge tone={statusTone(tenant.status)}>{statusLabel(tenant.status)}</Badge> ·{' '}
            {tenant.accountManager ? (
              <>
                {isStaff ? 'Managed by' : 'Your Balsam contact:'}{' '}
                <strong>{tenant.accountManager.email}</strong>
              </>
            ) : (
              'No account manager'
            )}{' '}
            · created {formatDate(tenant.createdAt)}
          </>
        }
        actions={
          isStaff ? (
            <Button variant="secondary" loading={switchTenant.isPending} onClick={workOn}>
              Work on this tenant
            </Button>
          ) : undefined
        }
      />
      {notice ? <Alert kind="success">{notice}</Alert> : null}
      {switchTenant.error ? <Alert kind="error">{errorMessage(switchTenant.error)}</Alert> : null}

      <div className="stats">
        <Stat label="Accounts" value={members.length} reference={`of ${TENANT_MAX_MEMBERS}`} />
        <Stat label="Email domains" value={domains.length} reference={`${activeDomains} active`} />
        <Stat label="Solutions" value={live.length} reference="live plans" />
        <Stat
          label="WhatsApp"
          value={account?.displayPhoneNumber ?? 'not connected'}
          reference={account ? ACCOUNT_STATUS_COPY[account.status].label : undefined}
        />
      </div>

      <div className="grid grid--2">
        <TenantSettings tenant={tenant} owner={owner} isStaff={isStaff} onSaved={setNotice} />

        <TenantTeam
          tenantId={tenant.id}
          canManage={isStaff ? can(me, 'tenants:write') : can(me, 'tenant:team:manage')}
          canDeactivate={isStaff && can(me, 'users:support')}
          selfUserId={me.user.id}
        />
      </div>

      <Section
        title="Email domains"
        meta={`${domains.length} assigned`}
        actions={<Link to={links.domains}>{isStaff ? 'Email domains' : 'Email'}</Link>}
      >
        {domains.length === 0 ? (
          <EmptyState
            title="No email domain yet"
            body={
              isStaff
                ? 'Assign one from Email domains; sign-up opens to everyone at the domain.'
                : 'Balsam assigns your company domain during onboarding.'
            }
          />
        ) : (
          <DataTable
            label="Email domains"
            columns={[
              { key: 'domain', header: 'Domain' },
              { key: 'status', header: 'Status' },
            ]}
          >
            {domains.map((d) => (
              <tr key={d.id}>
                <td>
                  <strong>{d.domain}</strong>
                </td>
                <td>
                  <Badge tone={statusTone(d.status)}>{statusLabel(d.status)}</Badge>
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </Section>

      <Section
        title="Solutions & plans"
        meta="What Balsam runs for this tenant and the plan it is on"
        actions={links.plans ? <Link to={links.plans}>Subscription</Link> : undefined}
      >
        {solutions.isError ? (
          <ErrorState
            error={solutions.error}
            onRetry={() => solutions.refetch()}
            retrying={solutions.isFetching}
          />
        ) : null}
        {solutions.isPending ? <SkeletonTable rows={2} cols={5} /> : null}
        {solutions.data ? (
          <DataTable
            label="Solutions and plans"
            columns={[
              { key: 'solution', header: 'Solution' },
              { key: 'provider', header: 'Provider' },
              { key: 'plan', header: 'Plan' },
              { key: 'status', header: 'Status' },
              { key: 'renews_ends', header: 'Renews / ends' },
            ]}
          >
            {solutions.data.map((s) => {
              const sub = liveFor(s.slug);
              return (
                <tr key={s.slug}>
                  <td>{s.name}</td>
                  <td>{s.provider}</td>
                  <td>{sub ? sub.plan.name : s.includedWithOnboarding ? 'Included' : '—'}</td>
                  <td>
                    {sub ? (
                      <Badge tone={subscriptionTone(sub.status)}>{statusLabel(sub.status)}</Badge>
                    ) : s.includedWithOnboarding ? (
                      <Badge tone="accent">Included</Badge>
                    ) : (
                      <Badge tone="neutral">Not enabled</Badge>
                    )}
                  </td>
                  <td>
                    {sub
                      ? sub.trialEnd && sub.status === 'TRIALING'
                        ? `trial ends ${formatDate(sub.trialEnd)}`
                        : formatDate(sub.currentPeriodEnd)
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </DataTable>
        ) : null}
      </Section>

      <Card title="Onboarding" subtitle="What the platform plan includes and what is switched on">
        {!onboarding.subscription ? (
          <EmptyState
            title="No platform plan yet"
            body={
              isStaff
                ? 'The checklist follows the platform plan once the tenant is on one.'
                : 'Your checklist appears once Balsam puts the organization on a platform plan.'
            }
          />
        ) : (
          <div className="stack">
            <div className="stats">
              <Stat
                label="Activated"
                value={progress.activated}
                reference={`of ${progress.total}`}
              />
              <Stat label="Detected" value={progress.detected} reference="seen by the hub" />
              <Stat
                label="Remaining"
                value={progress.total - progress.activated}
                reference="still to activate"
              />
            </div>
            {nextUp.length ? (
              <div>
                <p className="checklist__heading">Next up</p>
                <ul className="pending-items">
                  {nextUp.map((item) => (
                    <li key={item.key}>
                      {item.label}
                      {typeof item.value === 'string' ? (
                        <>
                          {' '}
                          <code>{item.value}</code>
                        </>
                      ) : null}
                      {item.detected ? <span className="muted"> · detected</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="muted">Everything in the plan is activated.</p>
            )}
          </div>
        )}
      </Card>

      <Card
        title="WhatsApp"
        subtitle="The connected number and its standing with Meta"
        actions={<Link to={links.whatsapp}>WhatsApp</Link>}
      >
        <dl className="kv">
          <dt>Number</dt>
          <dd>
            {account ? (
              <>
                <strong>{account.displayPhoneNumber ?? account.phoneNumberId}</strong>
                {account.verifiedName ? (
                  <span className="muted"> · {account.verifiedName}</span>
                ) : null}
              </>
            ) : (
              <span className="muted">not connected</span>
            )}
          </dd>
          <dt>Status</dt>
          <dd>
            {account ? (
              <Badge tone={ACCOUNT_STATUS_COPY[account.status].tone}>
                {ACCOUNT_STATUS_COPY[account.status].label}
              </Badge>
            ) : (
              <Badge tone="neutral">No number</Badge>
            )}
          </dd>
          <dt>Quality</dt>
          <dd>{account ? <QualityBadge rating={account.qualityRating} /> : '—'}</dd>
          <dt>Plan</dt>
          <dd>
            {whatsapp.subscription ? (
              <>
                {whatsapp.subscription.plan.name}{' '}
                <Badge tone={subscriptionTone(whatsapp.subscription.status)}>
                  {statusLabel(whatsapp.subscription.status)}
                </Badge>
              </>
            ) : (
              <span className="muted">no WhatsApp plan</span>
            )}
          </dd>
          <dt>Last synced</dt>
          <dd>{formatDate(account?.lastSyncedAt)}</dd>
        </dl>
      </Card>
    </>
  );
}

/**
 * The tenant's own facts. Staff with tenants:write rename it and change its
 * status; admins (tenants:assign) reassign the account manager and can
 * offboard it. A tenant account reads the same card.
 */
function TenantSettings({
  tenant,
  owner,
  isStaff,
  onSaved,
}: {
  tenant: Tenant;
  owner: { firstName: string | null; lastName: string | null; email: string } | null;
  isStaff: boolean;
  onSaved: (text: ReactNode) => void;
}) {
  const me = useMe();
  const navigate = useNavigate();
  const canEdit = isStaff && can(me, 'tenants:write');
  const canAssign = isStaff && can(me, 'tenants:assign');
  const canOffboard = hasAccess(me, 'admin');
  const [editing, setEditing] = useState(false);
  const [offboarding, setOffboarding] = useState(false);
  const remove = useDeleteTenant();

  return (
    <Card
      title="Settings"
      subtitle={isStaff ? 'The organization as the hub knows it' : 'Your organization on the hub'}
      actions={
        canEdit ? (
          <Button className="btn--small" variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        ) : undefined
      }
    >
      <dl className="kv">
        <dt>Name</dt>
        <dd>{tenant.name}</dd>
        <dt>Slug</dt>
        <dd>
          <code>{tenant.slug}</code>
        </dd>
        <dt>Status</dt>
        <dd>
          <Badge tone={statusTone(tenant.status)}>{statusLabel(tenant.status)}</Badge>
        </dd>
        <dt>Owner</dt>
        <dd>
          {owner ? (
            <>
              {displayName(owner)} <span className="muted">· {owner.email}</span>
            </>
          ) : tenant.tenantOwnerId ? (
            <code title={tenant.tenantOwnerId}>{shortId(tenant.tenantOwnerId)}</code>
          ) : tenant.ownerEmail ? (
            <>
              {tenant.ownerEmail} <span className="muted">· not signed up yet</span>
            </>
          ) : (
            <span className="muted">None yet</span>
          )}
        </dd>
        <dt>{isStaff ? 'Account manager' : 'Balsam contact'}</dt>
        <dd>
          <AccountManager
            tenant={tenant}
            canAssign={canAssign}
            onChanged={() => onSaved(<>Account manager updated.</>)}
          />
        </dd>
        <dt>Tenant id</dt>
        <dd>
          <code>{tenant.id}</code>
        </dd>
        <dt>Updated</dt>
        <dd>
          {formatDate(tenant.lastUpdatedAt)} <span className="muted">· v{tenant.version}</span>
        </dd>
      </dl>
      {canOffboard ? (
        <div className="row-actions">
          <Button variant="danger" className="btn--small" onClick={() => setOffboarding(true)}>
            Offboard tenant
          </Button>
        </div>
      ) : null}

      {editing ? (
        <EditTenantModal
          tenant={tenant}
          onClose={() => setEditing(false)}
          onDone={(next) => {
            setEditing(false);
            onSaved(
              <>
                Saved <strong>{next.name}</strong>.
              </>,
            );
          }}
        />
      ) : null}
      {offboarding ? (
        <Modal
          open
          onClose={() => !remove.isPending && setOffboarding(false)}
          title={`Offboard ${tenant.name}?`}
          footer={
            <>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOffboarding(false)}
                disabled={remove.isPending}
              >
                Keep it
              </Button>
              <Button
                type="button"
                variant="danger"
                loading={remove.isPending}
                onClick={() =>
                  remove.mutate(tenant.id, {
                    onSuccess: () =>
                      navigate('/staff/tenants', {
                        replace: true,
                        state: { notice: `${tenant.name} was offboarded.` },
                      }),
                  })
                }
              >
                Offboard {tenant.name}
              </Button>
            </>
          }
        >
          {remove.error ? <Alert kind="error">{errorMessage(remove.error)}</Alert> : null}
          <p>
            The tenant is closed: its sign-up entries stop admitting anyone, its email domains are
            unassigned, its mailboxes suspended, its plans canceled and running WhatsApp campaigns
            stopped. Nothing is deleted on mxroute or at Meta. The accounts keep their sign-in but
            lose their organization.
          </p>
        </Modal>
      ) : null}
    </Card>
  );
}

/** Name and status; slug and owner stay as they are. Mounted only while open. */
function EditTenantModal({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: (tenant: Tenant) => void;
}) {
  const [name, setName] = useState(tenant.name);
  const [status, setStatus] = useState<TenantStatus>(tenant.status);
  const formId = useId();
  const update = useUpdateTenant();
  const error = update.error ? errorMessage(update.error) : null;
  const changed = name.trim() !== tenant.name || status !== tenant.status;
  const valid = name.trim().length >= 2 && changed;

  const close = () => {
    if (!update.isPending) onClose();
  };
  const submit = () => {
    if (!valid || update.isPending) return;
    update.mutate(
      {
        tenantId: tenant.id,
        body: {
          version: tenant.version,
          ...(name.trim() !== tenant.name ? { name: name.trim() } : {}),
          ...(status !== tenant.status ? { status } : {}),
        },
      },
      { onSuccess: onDone },
    );
  };

  return (
    <Modal
      open
      onClose={close}
      title={`Edit ${tenant.name}`}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={close} disabled={update.isPending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={update.isPending} disabled={!valid}>
            Save
          </Button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form
        id={formId}
        className="form"
        aria-label={`Edit ${tenant.name}`}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        noValidate
      >
        <TextField
          label="Organization name"
          name="tenantName"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <SelectField
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as TenantStatus)}
          hint="SUSPENDED pauses the tenant's access; CLOSED ends it. Offboarding is a separate, deliberate step."
        >
          {TENANT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </SelectField>
      </form>
    </Modal>
  );
}
